/* ==========================================================================
   ELVA — Below the fold
   Behaviour shared by the content sections: carousel dots, comparison tabs
   and single-open accordions. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- rail */

  function Rail(root) {
    var rail = root.querySelector('[data-elva-rail]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-elva-rail-dot]'));
    if (!rail) return;

    var items = Array.prototype.slice.call(rail.children);
    var current = -1;

    function setCurrent(index) {
      if (index === current) return;
      current = index;
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function nearest() {
      var mid = rail.scrollLeft + rail.clientWidth / 2;
      var best = 0;
      var bestDist = Infinity;
      items.forEach(function (item, i) {
        var dist = Math.abs(item.offsetLeft + item.offsetWidth / 2 - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    var ticking = false;
    rail.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          setCurrent(nearest());
          ticking = false;
        });
      },
      { passive: true }
    );

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        var item = items[i];
        if (!item) return;
        rail.scrollTo({
          left: item.offsetLeft - (rail.clientWidth - item.offsetWidth) / 2,
          behavior: 'smooth'
        });
      });
    });

    var prev = root.querySelector('[data-elva-rail-prev]');
    var next = root.querySelector('[data-elva-rail-next]');

    function step(direction) {
      var width = items.length ? items[0].offsetWidth : rail.clientWidth;
      rail.scrollBy({ left: direction * (width + 14), behavior: 'smooth' });
    }

    function setEnds() {
      if (prev) prev.classList.toggle('is-hidden', rail.scrollLeft < 8);
      if (next) {
        next.classList.toggle(
          'is-hidden',
          rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 8
        );
      }
    }

    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    if (prev || next) {
      rail.addEventListener('scroll', setEnds, { passive: true });
      window.addEventListener('resize', setEnds);
      setEnds();
    }

    setCurrent(nearest());
  }

  /* ------------------------------------------------------------ lightbox */

  /* The cards are stills. One <video> serves the whole rail: its source is set
     on open and dropped on close, so a row of eight testimonials costs one
     download — the clip actually being watched. */
  function Lightbox(root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-elva-reel-open]'));
    var box = root.querySelector('[data-elva-lb]');
    if (!cards.length || !box) return;

    var video = box.querySelector('[data-elva-lb-video]');
    var caption = box.querySelector('[data-elva-lb-caption]');
    var closeBtn = box.querySelector('.elva-lb__close');
    var prev = box.querySelector('[data-elva-lb-prev]');
    var next = box.querySelector('[data-elva-lb-next]');
    if (!video) return;

    var index = 0;
    var opener = null;

    if (cards.length < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
    }

    function show(i) {
      index = (i + cards.length) % cards.length;
      var card = cards[index];

      video.pause();
      video.setAttribute('src', card.getAttribute('data-src'));
      video.muted = false;
      if (caption) caption.textContent = card.getAttribute('data-caption') || '';

      /* A browser that refuses a sound-on start still leaves the native
         controls sitting there, so a rejected promise is not a failure. */
      var started = video.play();
      if (started && started.catch) started.catch(function () {});
    }

    function open(i, from) {
      opener = from || null;
      box.hidden = false;
      document.documentElement.classList.add('elva-lb-open');
      show(i);
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    function close() {
      video.pause();
      video.removeAttribute('src');
      video.load();               /* drops the buffer instead of leaving it resident */
      box.hidden = true;
      document.documentElement.classList.remove('elva-lb-open');
      if (opener) opener.focus({ preventScroll: true });
      opener = null;
    }

    cards.forEach(function (card, i) {
      card.addEventListener('click', function () {
        open(i, card);
      });
    });

    box.querySelectorAll('[data-elva-lb-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    document.addEventListener('keydown', function (event) {
      if (box.hidden) return;
      if (event.key === 'Escape') {
        close();
      } else if (cards.length > 1 && event.key === 'ArrowLeft') {
        show(index - 1);
      } else if (cards.length > 1 && event.key === 'ArrowRight') {
        show(index + 1);
      }
    });

    /* Swiping is how a phone expects to move between clips. */
    var startX = null;
    var startY = null;

    box.addEventListener('touchstart', function (event) {
      startX = event.changedTouches[0].clientX;
      startY = event.changedTouches[0].clientY;
    }, { passive: true });

    box.addEventListener('touchend', function (event) {
      if (startX === null || cards.length < 2) return;
      var dx = event.changedTouches[0].clientX - startX;
      var dy = event.changedTouches[0].clientY - startY;
      startX = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        show(index + (dx < 0 ? 1 : -1));
      }
    }, { passive: true });
  }

  /* ---------------------------------------------------------------- tabs */

  function Tabs(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-elva-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-elva-panel]'));
    if (!tabs.length) return;

    function show(key) {
      tabs.forEach(function (tab) {
        tab.setAttribute('aria-selected', tab.getAttribute('data-elva-tab') === key ? 'true' : 'false');
      });
      panels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-elva-panel') !== key;
      });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        show(tab.getAttribute('data-elva-tab'));
      });
    });

    show(tabs[0].getAttribute('data-elva-tab'));
  }

  /* ----------------------------------------------------------- accordion */

  function Accordion(group) {
    if (group.getAttribute('data-elva-accordion') !== 'single') return;

    var items = Array.prototype.slice.call(group.querySelectorAll('details'));
    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        items.forEach(function (other) {
          if (other !== item) other.open = false;
        });
      });
    });
  }

  /* ----------------------------------------------------------------- init */

  function init(scope) {
    var root = scope || document;

    root.querySelectorAll('[data-elva-section]').forEach(function (el) {
      if (el.dataset.elvaReady) return;
      el.dataset.elvaReady = '1';
      Rail(el);
      Tabs(el);
      Lightbox(el);
    });

    root.querySelectorAll('[data-elva-accordion]').forEach(function (el) {
      if (el.dataset.elvaAccReady) return;
      el.dataset.elvaAccReady = '1';
      Accordion(el);
    });

    root.querySelectorAll('video[data-elva-video]').forEach(function (el) {
      if (el.dataset.elvaVidReady) return;
      el.dataset.elvaVidReady = '1';
      Autoplay(el);
    });
  }

  /* ------------------------------------------------------------ autoplay */

  /* Six clips autoplaying at once would download on page load and hold the
     first paint. Each one waits until it is actually on screen instead. */
  function Autoplay(video) {
    video.muted = true;            /* set again in JS: iOS ignores autoplay
                                      on a video it considers unmuted */
    if (!('IntersectionObserver' in window)) {
      video.play().catch(function () {});
      return;
    }

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.25 }).observe(video);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
